/** Sobrevive remounts (Strict Mode) — evita recargas duplicadas del catálogo POS. */
type InflightEntry = {
  promise: Promise<unknown[]>;
  resolve: (rows: unknown[]) => void;
  reject: (error: unknown) => void;
};

let hydratedCriteriaKey: string | null = null;
let hydratedProducts: unknown[] = [];
const inflightByKey = new Map<string, InflightEntry>();

export type PosCatalogLoadMode = 'initial' | 'background' | 'deduped';

function createInflight(criteriaKey: string): InflightEntry {
  let resolve!: (rows: unknown[]) => void;
  let reject!: (error: unknown) => void;
  const promise = new Promise<unknown[]>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  const entry: InflightEntry = { promise, resolve, reject };
  inflightByKey.set(criteriaKey, entry);
  return entry;
}

export function beginPosCatalogLoad(criteriaKey: string): PosCatalogLoadMode {
  if (inflightByKey.has(criteriaKey)) {
    return 'deduped';
  }
  if (hydratedCriteriaKey === criteriaKey) {
    return 'background';
  }
  createInflight(criteriaKey);
  return 'initial';
}

export function armPosCatalogInflight(criteriaKey: string): void {
  if (!inflightByKey.has(criteriaKey)) {
    createInflight(criteriaKey);
  }
}

export function completePosCatalogLoad(criteriaKey: string, products: readonly unknown[]): void {
  hydratedCriteriaKey = criteriaKey;
  hydratedProducts = [...products];
  const entry = inflightByKey.get(criteriaKey);
  if (entry) {
    entry.resolve(hydratedProducts);
    inflightByKey.delete(criteriaKey);
  }
}

export function failPosCatalogLoad(criteriaKey: string, error: unknown = new Error('catalog load failed')): void {
  const entry = inflightByKey.get(criteriaKey);
  if (entry) {
    entry.reject(error);
    inflightByKey.delete(criteriaKey);
  }
}

export function isPosCatalogHydrated(criteriaKey: string): boolean {
  return hydratedCriteriaKey === criteriaKey;
}

export function isPosCatalogInflight(criteriaKey: string): boolean {
  return inflightByKey.has(criteriaKey);
}

export function getPosCatalogCachedProducts<T>(criteriaKey: string): T[] | null {
  if (hydratedCriteriaKey !== criteriaKey || hydratedProducts.length === 0) {
    return null;
  }
  return hydratedProducts as T[];
}

export function waitForPosCatalogLoad<T>(criteriaKey: string): Promise<T[]> {
  const cached = getPosCatalogCachedProducts<T>(criteriaKey);
  if (cached) {
    return Promise.resolve(cached);
  }
  const entry = inflightByKey.get(criteriaKey);
  if (!entry) {
    return Promise.resolve([]);
  }
  return entry.promise as Promise<T[]>;
}

export function invalidatePosCatalogLoadGate(): void {
  for (const [, entry] of inflightByKey) {
    entry.reject(new Error('catalog load invalidated'));
  }
  inflightByKey.clear();
  hydratedCriteriaKey = null;
  hydratedProducts = [];
}
